import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  FileText, MessageSquare, Book, Shield, Clock, X, Search, CheckCircle,
  Sparkles, Plus, ArrowUp, ChevronRight, ChevronDown, ChevronLeft, Download,
  Type, AlignLeft, AlignCenter, AlignRight, AlignJustify, Undo, Redo, Eraser
} from 'lucide-react';
import MobileBottomSheet from './MobileBottomSheet';
import DocumentCard from '../ui/DocumentCard';
import { getTheme } from '../../utils/theme';
import { loadGooglePicker, downloadDriveFile } from '../../utils/googleDrivePicker';

import { useChat } from '../../contexts/ChatContext';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import ReactMarkdown from 'react-markdown';
import { uploadFileAndPrepareData } from '../../utils/fileUpload';
import { useAuth } from '../../contexts/AuthContext';
import { useDocuments } from '../../contexts/DocumentContext';
import EditCanvas from './EditCanvas';

const ContlyView = ({ openedDocument, onDocumentOpened, onDocumentClick, onClose, rightSidebarOpen, onCloseDocuments, triggerUpload, onUploadTriggered }) => {
  const { messages, sendMessage, isLoading, streamingMessage, currentSessionId } = useChat();
  const { currentUser } = useAuth();
  const { documents, uploadDocument, updateDocument } = useDocuments();
  const fileInputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [splitViewActive, setSplitViewActive] = useState(false);
  const [documentTitle, setDocumentTitle] = useState('Non-disclosure agreement');
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [showThinking, setShowThinking] = useState(false);
  const [showAttachDropdown, setShowAttachDropdown] = useState(false);
  const [showCloudSubmenu, setShowCloudSubmenu] = useState(false);
  const [showMobileDocuments, setShowMobileDocuments] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [contlyFilter, setContlyFilter] = useState('all');
  const [contlySearchQuery, setContlySearchQuery] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [editorContent, setEditorContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const chatHistoryRef = useRef(null);
  const editorRef = useRef(null);
  const [editorReady, setEditorReady] = useState(false);
  const [splitPanelWidth, setSplitPanelWidth] = useState(50); // Percentage
  const [isResizing, setIsResizing] = useState(false);
  const [isEditCanvasOpen, setIsEditCanvasOpen] = useState(false);

  const attachDropdownRefMobile = useRef(null);
  const attachButtonRefMobile = useRef(null);
  const attachDropdownRefDesktop = useRef(null);
  const attachButtonRefDesktop = useRef(null);
  const docPageRef = useRef(null);
  const lastAppliedMessageIdRef = useRef(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [toolbarState, setToolbarState] = useState({
    fontName: 'Inter, sans-serif',
    fontSize: '3',
    formatBlock: 'p'
  });

  // Detect mobile (768px breakpoint)
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isSidebarUpload = useRef(false);

  // Handle external upload trigger (from sidebar)
  useEffect(() => {
    if (triggerUpload && fileInputRef.current) {
      isSidebarUpload.current = true;
      fileInputRef.current.click();
      onUploadTriggered?.();
    }
  }, [triggerUpload, onUploadTriggered]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatHistoryRef.current && (messages.length > 0 || streamingMessage)) {
      requestAnimationFrame(() => {
        if (chatHistoryRef.current) {
          chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
        }
      });
    }
  }, [messages, streamingMessage]);

  // Ref to track which document ID is currently loaded in the editor
  const loadedDocIdRef = useRef(null);

  // Handle openedDocument prop change
  useEffect(() => {
    if (openedDocument) {
      if (loadedDocIdRef.current !== openedDocument.id) {
        setDocumentTitle(openedDocument.title || openedDocument.name || openedDocument.filename);
        const hasContent = openedDocument.content !== undefined && openedDocument.content !== null;
        const content = hasContent
          ? openedDocument.content
          : `<h1>${openedDocument.title || openedDocument.name || 'Document'}</h1><p>Start editing this document...</p>`;

        setEditorContent(content);
        loadedDocIdRef.current = openedDocument.id;
        setSplitViewActive(true);
        if (isMobile) {
          setShowMobileDocuments(false);
        }
        // Explicitly update ref innerHTML on document load
        if (docPageRef.current) {
          docPageRef.current.innerHTML = content;
        }
      }
      onDocumentOpened?.(openedDocument);
    } else {
      loadedDocIdRef.current = null;
      setSplitViewActive(false);
      setEditorContent('');
      setDocumentTitle('New Document');
    }
  }, [openedDocument, onDocumentOpened, isMobile]);

  // Helper for downloading content as DOCX using docx library
  const downloadAsDocx = async (content, title = 'document', convertMarkdown = false) => {
    console.log('[Contly Export] OVERHAULED EXPORT: Starting export with improved docx logic...');

    if (!content || content.trim() === '' || content === '<br>') {
      alert('Cannot export an empty document.');
      return;
    }

    try {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;

      const paragraphs = [];

      // Recursive function to parse DOM nodes into docx elements
      const parseNodeArr = (nodes, currentRunProps = {}) => {
        const results = [];

        nodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent;
            if (text) {
              results.push(new TextRun({
                text,
                ...currentRunProps,
                size: 22, // 11pt
                font: "Arial"
              }));
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const tag = node.tagName.toLowerCase();

            // Handle inline formatting
            const nextRunProps = { ...currentRunProps };
            if (tag === 'strong' || tag === 'b') nextRunProps.bold = true;
            if (tag === 'em' || tag === 'i') nextRunProps.italics = true;
            if (tag === 'u') nextRunProps.underline = {};

            if (tag === 'br') {
              results.push(new TextRun({ text: "", break: 1 }));
            } else if (['span', 'strong', 'b', 'em', 'i', 'u', 'font'].includes(tag)) {
              // Recurse for inline elements and flatten results
              results.push(...parseNodeArr(node.childNodes, nextRunProps));
            } else if (['p', 'h1', 'h2', 'h3', 'li', 'div'].includes(tag)) {
              // Block element found within an inline context? 
              // (Shouldn't happen with clean HTML, but let's handle by flattening)
              results.push(...parseNodeArr(node.childNodes, nextRunProps));
            }
          }
        });
        return results;
      };

      // Main block processor
      const processBlock = (node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        const tag = node.tagName.toLowerCase();

        // Handle list containers (bypass and process children)
        if (tag === 'ul' || tag === 'ol') {
          Array.from(node.childNodes).forEach(processBlock);
          return;
        }

        if (['p', 'h1', 'h2', 'h3', 'li', 'div'].includes(tag)) {
          const children = parseNodeArr(node.childNodes);
          if (children.length === 0) return;

          let heading = undefined;
          if (tag === 'h1') heading = HeadingLevel.HEADING_1;
          if (tag === 'h2') heading = HeadingLevel.HEADING_2;
          if (tag === 'h3') heading = HeadingLevel.HEADING_3;

          paragraphs.push(new Paragraph({
            children,
            heading,
            spacing: {
              before: tag.startsWith('h') ? 300 : 150,
              after: 150,
              line: 360, // 1.5 line spacing
            },
            bullet: tag === 'li' ? { level: 0 } : undefined
          }));
        } else {
          // If it's something else, try to parse its children as potential blocks
          Array.from(node.childNodes).forEach(processBlock);
        }
      };

      // Start processing from top-level nodes
      Array.from(tempDiv.childNodes).forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          processBlock(node);
        } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
          // Orphan text at top level
          paragraphs.push(new Paragraph({
            children: [new TextRun({ text: node.textContent.trim(), size: 22, font: "Arial" })],
            spacing: { after: 120, line: 360 }
          }));
        }
      });

      if (paragraphs.length === 0) {
        throw new Error('Could not identify any document content.');
      }

      const doc = new Document({
        sections: [{
          properties: {
            page: {
              margin: {
                top: 1440, // 1 inch
                right: 1440,
                bottom: 1440,
                left: 1440,
              },
            },
          },
          children: paragraphs
        }]
      });

      const blob = await Packer.toBlob(doc);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title}.docx`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

    } catch (error) {
      console.error('[Contly Export] Improved export failed:', error);
      alert('Export failed: ' + error.message);
    }
  };

  // Auto-populate editor from AI response
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];

      if (lastMessage.role === 'assistant' && lastMessage.id !== lastAppliedMessageIdRef.current) {
        // Match both document-update and document-append
        const updateMatch = lastMessage.content.match(/```document-update(?:.*)?\s*([\s\S]*?)```/i);
        const appendMatch = lastMessage.content.match(/```document-append(?:.*)?\s*([\s\S]*?)```/i);

        if (updateMatch?.[1]) {
          console.log('[Contly] Auto-applying document update');
          handleApplyToDocument(updateMatch[1], 'replace');
          lastAppliedMessageIdRef.current = lastMessage.id;
        } else if (appendMatch?.[1]) {
          console.log('[Contly] Auto-applying document append');
          handleApplyToDocument(appendMatch[1], 'append');
          lastAppliedMessageIdRef.current = lastMessage.id;
        }
      }
    }
  }, [messages]);

  // Close split view when session changes (e.g. New Chat)
  useEffect(() => {
    if (splitViewActive) {
      setSplitViewActive(false);
    }
  }, [currentSessionId]);

  const handleExport = async () => {
    // Try to get current content from DOM first for the absolute latest version
    let contentToExport = docPageRef.current ? docPageRef.current.innerHTML : editorContent;

    // Fallback and cleaning if DOM is empty
    if (!contentToExport || contentToExport.trim() === '' || contentToExport === '<br>') {
      contentToExport = editorContent;
    }

    await downloadAsDocx(contentToExport, documentTitle || 'document', false);
  };


  const contentRef = useRef(editorContent);
  const isDirtyRef = useRef(false);

  useEffect(() => {
    contentRef.current = editorContent;
    if (openedDocument && editorContent !== openedDocument.content) {
      isDirtyRef.current = true;
    } else {
      isDirtyRef.current = false;
    }
  }, [editorContent, openedDocument]);

  const saveDocument = async (contentToSave) => {
    if (!openedDocument || !contentToSave) return;
    setIsSaving(true);
    try {
      await updateDocument(openedDocument.id, { content: contentToSave });
      isDirtyRef.current = false;
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!openedDocument || !editorContent) return;
    if (editorContent === openedDocument.content) return;
    const timer = setTimeout(() => {
      saveDocument(editorContent);
    }, 2000);
    return () => clearTimeout(timer);
  }, [editorContent, openedDocument, updateDocument]);

  useEffect(() => {
    return () => {
      if (isDirtyRef.current && contentRef.current && openedDocument) {
        updateDocument(openedDocument.id, { content: contentRef.current })
          .catch(err => console.error('Cleanup save failed:', err));
      }
    };
  }, [openedDocument]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        attachDropdownRefDesktop.current &&
        !attachDropdownRefDesktop.current.contains(event.target) &&
        !attachButtonRefDesktop.current.contains(event.target)
      ) {
        setShowAttachDropdown(false);
      }
      if (
        showCloudSubmenu &&
        !event.target.closest('#contlyCloudBtn') &&
        !event.target.closest('#contlyCloudSubmenu')
      ) {
        setShowCloudSubmenu(false);
      }
    };
    const handleDocumentClickGlobal = (event) => {
      if (!event.target.closest('.toolbar-dropdown-container')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('click', handleDocumentClickGlobal);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('click', handleDocumentClickGlobal);
    };
  }, [showCloudSubmenu]);

  const handleApplyToDocument = (content, mode = 'append') => {
    if (!splitViewActive) setSplitViewActive(true);

    // 1. Improved Markdown to HTML conversion
    // Headings
    let html = content
      .replace(/^### (.*$)/gm, '<h3>$1</h3>')
      .replace(/^## (.*$)/gm, '<h2>$1</h2>')
      .replace(/^# (.*$)/gm, '<h1>$1</h1>');

    // Bold/Italic
    html = html
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');

    // Lists (handle bullets)
    html = html.replace(/^\s*[\*\-]\s+(.*)/gm, '<li>$1</li>');

    // Group adjacent <li> into <ul>
    html = html.replace(/(<li>.*<\/li>[\n\r]*)+/g, (match) => {
      return `<ul>${match.trim()}</ul>`;
    });

    // Paragraphs and Breaks
    const blocks = html.split(/\n\n+/);
    const processedBlocks = blocks.map(block => {
      block = block.trim();
      if (!block) return '';
      if (block.startsWith('<h') || block.startsWith('<ul') || block.startsWith('<ol') || block.startsWith('<li') || block.startsWith('<p')) {
        return block;
      }
      return `<p>${block.replace(/\n/g, '<br>')}</p>`;
    });

    const formattedContent = processedBlocks.join('');

    if (mode === 'replace') {
      console.log('[Contly] Replacing document content with AI version');
      setEditorContent(formattedContent);

      requestAnimationFrame(() => {
        if (docPageRef.current) {
          docPageRef.current.innerHTML = formattedContent;
          setEditorReady(true);
        }
      });
    }
    else {
      console.log('[Contly] Appending AI content to document');
      const newContent = editorContent + formattedContent;
      setEditorContent(newContent);
      setTimeout(() => {
        if (docPageRef.current) docPageRef.current.innerHTML = newContent;
      }, 10);
    }
  };

  // Force sync content to DOM when split view becomes active or content changes
  useEffect(() => {
    if (splitViewActive && docPageRef.current && editorContent) {
      if (docPageRef.current.innerHTML !== editorContent) {
        console.log('Syncing editor content to DOM');
        docPageRef.current.innerHTML = editorContent;
      }
    }
  }, [splitViewActive, editorContent]);

  const cleanHtml = (html) => {
    return html
      .replace(/<[^>]*>?/gm, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const handleSend = async () => {
    if (!inputValue.trim() && pendingAttachments.length === 0) return;
    const content = inputValue.trim();
    setInputValue('');
    const attachments = [...pendingAttachments];
    setPendingAttachments([]);

    const steps = [
      { id: 1, title: 'Analyzing legal context', desc: 'Processing request...', status: 'pending' },
      { id: 2, title: 'Reviewing precedents', desc: 'Checking database...', status: 'pending' },
      { id: 3, title: 'Formulating advice', desc: 'Drafting response...', status: 'pending' },
    ];
    setThinkingSteps(steps);
    setShowThinking(true);

    steps.forEach((step, index) => {
      setTimeout(() => {
        setThinkingSteps(prev => prev.map(s => s.id === step.id ? { ...s, status: 'active' } : s));
        setTimeout(() => {
          setThinkingSteps(prev => prev.map(s => s.id === step.id ? { ...s, status: 'completed' } : s));
        }, 300);
      }, (index + 1) * 400);
    });

    let contextPrompt = `You are Contly, an expert AI legal assistant. Your goal is to help users with contracts, legal documents, and compliance.
    Provide professional, accurate, and concise legal insights. Always clarify that you are an AI and your advice is for informational purposes only.
    
    CRITICAL INSTRUCTIONS FOR DOCUMENT UPDATES:
    - If the user asks you to DRAFT, WRITE, CREATE, GENERATE, or perform a FULL EDIT/REVISION of a document, you MUST wrap the FULL updated content in:
    \`\`\`document-update
    [The full document text goes here, formatted in Markdown/HTML]
    \`\`\`
    - If the user asks to ADD, INSERT, APPEND a clause, or just propose a NEW section, wrap ONLY that specific part in:
    \`\`\`document-append
    [Only the new content here]
    \`\`\`
    - ALWAYS put your conversational response (explanations, "Sure!", "Based on your request...") OUTSIDE of these code blocks.
    - NEVER include conversational text inside the code blocks. The code blocks should contain ONLY the document content.
    - If the user says "edit my contract" or similar, use document-update with the WHOLE contract containing the changes.
    - If the user says "add this clause", use document-append.
    - For formatting: Use # for h1, ## for h2, ### for h3, **bold**, and *italic*.
    `;

    try {
      const finalAttachments = [...attachments];
      if (splitViewActive && openedDocument) {
        contextPrompt += `\n\n[Active Working Document: ${openedDocument.title || openedDocument.name}]\nContent:\n${cleanHtml(editorContent)}\n\n`;
        if (!finalAttachments.find(a => a.id === openedDocument.id)) {
          finalAttachments.push({
            id: openedDocument.id,
            name: openedDocument.title || openedDocument.name,
            mimeType: openedDocument.mimeType,
            url: openedDocument.url
          });
        }
      }
      finalAttachments.forEach(att => {
        if (openedDocument && att.id === openedDocument.id) return;
        const fullDoc = documents?.find(d => d.id === att.id);
        if (fullDoc && fullDoc.content) {
          contextPrompt += `\n\n[Attached Document: ${fullDoc.title || fullDoc.name}]\nContent:\n${cleanHtml(fullDoc.content)}\n\n`;
        }
      });

      // Filter attachments for Gemini backend to avoid 400 errors
      // Gemini can't handle docx binary data, so we only send actual compatible attachments
      const geminiCompatibleAttachments = finalAttachments.filter(att => {
        const mime = att.mimeType?.toLowerCase() || '';
        return !(mime.includes('word') || mime.includes('officedocument') || mime.includes('docx'));
      });

      console.log('[ContlyView] Sending message. Compatible attachments:', geminiCompatibleAttachments.length, 'of', finalAttachments.length);
      await sendMessage(content, contextPrompt, geminiCompatibleAttachments);
    } finally {
      setShowThinking(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleGoogleDrivePick = async () => {
    try {
      if (!currentUser) return;
      const file = await loadGooglePicker(currentUser.accessToken);
      if (!file) return;

      // Restriction: Only allow DOCX
      if (file.mimeType !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        alert('Only Microsoft Word (.docx) files are allowed in Contly.');
        return;
      }

      setShowThinking(true);
      setThinkingSteps([
        { id: 1, title: 'Downloading from Drive', desc: 'Fetching file content...', status: 'active' },
        { id: 2, title: 'Analyzing document', desc: 'Processing content...', status: 'pending' }
      ]);
      const fileContent = await downloadDriveFile(file.id, file.mimeType, currentUser.accessToken);
      const fileData = {
        name: file.name,
        mimeType: file.mimeType,
        data: fileContent,
        url: file.url,
        id: file.id
      };
      setPendingAttachments(prev => [...prev, fileData]);
    } catch (error) {
      console.error('Error handling Google Drive pick:', error);
    } finally {
      setShowThinking(false);
      setShowCloudSubmenu(false);
      setShowAttachDropdown(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    // Restriction: Only allow DOCX
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.docx')) {
      alert('Only Microsoft Word (.docx) files are allowed in Contly.');
      // Reset input so user can try again
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const isSidebar = isSidebarUpload.current;
    isSidebarUpload.current = false;
    if (!isSidebar) {
      setShowThinking(true);
      setThinkingSteps([
        { id: 1, title: 'Uploading document', desc: 'Saving to secure storage...', status: 'active' },
        { id: 2, title: 'Analyzing content', desc: 'Processing with AI...', status: 'pending' },
      ]);
    }
    try {
      const docType = (contlyFilter !== 'all') ? contlyFilter : 'contracts';
      const fileData = await uploadDocument(file, 'contly', docType, { category: docType });
      setPendingAttachments(prev => [...prev, fileData]);
    } catch (err) {
      console.error('Contly file upload failed:', err);
    } finally {
      setShowThinking(false);
      setShowAttachDropdown(false);
    }
  };

  const suggestions = [
    { id: 1, text: 'Review my employment contract', category: 'CONTRACT REVIEW', icon: FileText },
    { id: 2, text: 'Draft a new NDA agreement', category: 'DOCUMENT CREATION', icon: FileText },
    { id: 3, text: 'Find risky clauses in document', category: 'LEGAL ANALYSIS', icon: Search },
    { id: 4, text: 'Check contract compliance', category: 'COMPLIANCE', icon: CheckCircle },
  ];

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    setTimeout(() => handleSend(), 100);
  };

  const handleDocumentClick = (doc) => {
    if (!doc) return;

    // Toggle off if already open
    if (splitViewActive && openedDocument && doc.id === openedDocument.id) {
      setSplitViewActive(false);
      onClose?.();
      return;
    }

    setDocumentTitle(doc.title || doc.name || doc.filename || 'Document');
    setSplitViewActive(true);
    if (isMobile) setShowMobileDocuments(false);
    if (onDocumentClick) onDocumentClick(doc);
    else if (onDocumentOpened) onDocumentOpened(doc);
  };

  const filteredDocs = (contlyFilter === 'all'
    ? documents?.filter(d => d.app === 'contly')
    : documents?.filter(doc => doc.type === contlyFilter && doc.app === 'contly')) || [];

  const displayDocs = filteredDocs.map(d => ({
    ...d,
    title: d.title || d.name,
    badge: d.badge || 'ready',
    skeleton: false
  }));

  const searchFilteredDocs = contlySearchQuery.trim()
    ? displayDocs.filter(doc => doc.title.toLowerCase().includes(contlySearchQuery.toLowerCase()))
    : displayDocs;

  const badgeStyles = {
    review: { color: '#FF9F43', border: 'rgba(255, 159, 67, 0.3)', background: 'rgba(255, 159, 67, 0.1)' },
    draft: { color: '#FDCB6E', border: 'rgba(253, 203, 110, 0.3)', background: 'rgba(253, 203, 110, 0.1)' },
    signed: { color: '#55EFC4', border: 'rgba(85, 239, 196, 0.3)', background: 'rgba(85, 239, 196, 0.1)' },
    ready: { color: '#a855f7', border: 'rgba(168, 85, 247, 0.3)', background: 'rgba(168, 85, 247, 0.1)' },
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);

    if (files.length > 0) {
      // Restriction: Only allow DOCX
      const validFiles = files.filter(f => f.name.toLowerCase().endsWith('.docx'));
      const invalidFiles = files.length - validFiles.length;

      if (invalidFiles > 0) {
        alert(`${invalidFiles} file(s) skipped. Only Microsoft Word (.docx) files are allowed in Contly.`);
      }

      if (validFiles.length === 0) return;

      setShowThinking(true);
      setThinkingSteps([
        { id: 1, title: 'Uploading document', desc: 'Saving...', status: 'active' },
        { id: 2, title: 'Analyzing', desc: 'Processing...', status: 'pending' },
      ]);
      try {
        const docType = (contlyFilter !== 'all') ? contlyFilter : 'contracts';
        const uploadedFiles = await Promise.all(validFiles.map(file => uploadDocument(file, 'contly', docType, { category: docType })));
        setPendingAttachments(prev => [...prev, ...uploadedFiles]);
      } finally {
        setShowThinking(false);
      }
      return;
    }
    const data = e.dataTransfer.getData('application/json');
    if (!data) return;
    const doc = JSON.parse(data);
    if (doc.url && doc.name) {
      setPendingAttachments(prev => [...prev, { mimeType: doc.mimeType, url: doc.url, name: doc.name, id: doc.id }]);
    }
  };

  const syncToolbarState = () => {
    if (!docPageRef.current) return;

    // Get current styles from the selection
    const font = document.queryCommandValue('fontName') || 'Inter, sans-serif';
    const size = document.queryCommandValue('fontSize') || '3';

    // For formatBlock, we need to check the parent nodes
    let format = 'p';
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      let node = selection.getRangeAt(0).startContainer;
      while (node && node !== docPageRef.current) {
        if (node.nodeType === 1) {
          const tag = node.tagName.toLowerCase();
          if (['h1', 'h2', 'h3', 'p'].includes(tag)) {
            format = tag;
            break;
          }
        }
        node = node.parentNode;
      }
    }

    setToolbarState({
      fontName: font.replace(/['"]/g, ''), // Clean up quotes
      fontSize: size,
      formatBlock: format
    });
  };

  // Resize handlers for split view
  const handleResizeMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleResizeMouseMove = (e) => {
    if (!isResizing) return;

    const container = document.getElementById('contlySplitView');
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

    // Limit between 20% and 80%
    if (newWidth >= 20 && newWidth <= 80) {
      setSplitPanelWidth(newWidth);
    }
  };

  const handleResizeMouseUp = () => {
    setIsResizing(false);
  };

  // Add global mouse event listeners for resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMouseMove);
      document.addEventListener('mouseup', handleResizeMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      // Add overlay to iframes or other elements that might trap mouse events if needed
      const iframes = document.querySelectorAll('iframe');
      iframes.forEach(iframe => iframe.style.pointerEvents = 'none');
    } else {
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleResizeMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      const iframes = document.querySelectorAll('iframe');
      iframes.forEach(iframe => iframe.style.pointerEvents = 'auto');
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleResizeMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const handleToolbarCommand = (command, value = null) => {
    if (docPageRef.current) {
      docPageRef.current.focus();
    }

    // Ensure styleWithCSS is on for consistent behavior
    document.execCommand('styleWithCSS', false, true);

    if (command === 'formatBlock') {
      // Standard execCommand for formatBlock doesn't use brackets for the value usually,
      // but some browsers like it differently. We'll use the standard tag name.
      document.execCommand(command, false, value);
    } else {
      document.execCommand(command, false, value);
    }

    if (docPageRef.current) {
      setEditorContent(docPageRef.current.innerHTML);
      syncToolbarState();
    }
    setActiveDropdown(null);
  };

  const ToolbarDropdown = ({ label, options, currentValue, onSelect, isOpen, onToggle, width = "80px" }) => {
    const buttonRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

    useEffect(() => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setCoords({
          top: rect.bottom + 4,
          left: rect.left,
          width: Math.max(rect.width, 100)
        });
      }
    }, [isOpen]);

    return (
      <div className="toolbar-dropdown-container" style={{ minWidth: width }}>
        <button
          ref={buttonRef}
          className="flex items-center justify-between w-full bg-[#1a1a1a] border border-white/10 rounded px-2 py-1.5 text-[10px] hover:bg-white/5 transition-colors whitespace-nowrap"
          onMouseDown={e => e.preventDefault()}
          onClick={onToggle}
        >
          <span className="truncate">{options.find(o => o.value === currentValue)?.label || label}</span>
          <ChevronDown size={10} className={`ml-1 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && createPortal(
          <div
            className="fixed bg-[#1a1a1a] border border-white/10 rounded-lg shadow-2xl z-[9999] overflow-hidden min-w-[120px]"
            style={{
              top: `${coords.top}px`,
              left: `${coords.left}px`,
              width: `${coords.width}px`
            }}
          >
            {options.map(opt => (
              <button
                key={opt.value}
                className={`w-full text-left px-3 py-2 text-[10px] hover:bg-white/5 transition-colors ${currentValue === opt.value ? 'text-blue-400 bg-white/5' : ''}`}
                onMouseDown={e => {
                  e.preventDefault();
                  onSelect(opt.value);
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>,
          document.body
        )}
      </div>
    );
  };

  return (
    <div id="view-contly" className="view active" onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}>
      <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileUpload} accept=".docx" />
      <div className="contly-content">
        <div className={`split-view-container ${splitViewActive ? 'split-view-active' : ''}`} id="contlySplitView">
          <div className="chat-interface" style={splitViewActive ? { width: isMobile ? '0%' : `${splitPanelWidth}%`, display: (isMobile && splitViewActive) ? 'none' : 'block', flex: 'none' } : {}}>
            <div className="chat-header">
              <span>Contly Assistant</span>
              {isMobile && (
                <button className="chat-header-docs-button" onClick={() => setShowMobileDocuments(true)}>
                  <FileText size={18} />
                </button>
              )}
            </div>
            <div className="chat-history" ref={chatHistoryRef}>
              {messages.length === 0 && !showThinking ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <MessageSquare size={48} />
                  </div>
                  <h1 className="empty-title">Start with Contly</h1>
                  <p className="empty-subtitle">Legal drafting and analysis</p>
                  <div className="mt-8 flex flex-col gap-2 w-full max-w-sm mx-auto">
                    {suggestions.map(s => (
                      <button key={s.id} onClick={() => handleSuggestionClick(s.text)} className="suggestion-card-base p-3 rounded-xl border flex items-center gap-3">
                        <s.icon size={18} />
                        <span className="text-sm">{s.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map(msg => {
                    const hasUpdate = msg.content.includes('```document-update');
                    const hasAppend = msg.content.includes('```document-append');

                    // Regex improvements: More flexible matching for blocks
                    const updateRegex = /```document-update\s*([\s\S]*?)```/i;
                    const appendRegex = /```document-append\s*([\s\S]*?)```/i;

                    const updateMatch = msg.content.match(updateRegex);
                    const appendMatch = msg.content.match(appendRegex);

                    const updateContent = updateMatch ? updateMatch[1] : null;
                    const appendContent = appendMatch ? appendMatch[1] : null;

                    // Clean message for display (hide technical blocks)
                    const cleanDisplayContent = msg.content
                      .replace(updateRegex, '')
                      .replace(appendRegex, '')
                      .trim();

                    return (
                      <div key={msg.id} className={`chat-message ${msg.role === 'user' ? 'asymmetric-user' : 'asymmetric-assistant'}`}>
                        {cleanDisplayContent ? (
                          <ReactMarkdown>{cleanDisplayContent}</ReactMarkdown>
                        ) : (
                          msg.role === 'assistant' && (hasUpdate || hasAppend) ?
                            <p className="text-xs opacity-50 italic">Proposed document changes:</p> : null
                        )}
                        {msg.role === 'assistant' && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {hasUpdate && updateContent && (
                              <button onClick={() => handleApplyToDocument(updateContent, 'replace')} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-500/20 text-blue-400 text-[10px] font-bold uppercase transition-all hover:bg-blue-500/30">
                                <Plus size={12} /> Apply Changes
                              </button>
                            )}
                            {hasAppend && appendContent && (
                              <button onClick={() => handleApplyToDocument(appendContent, 'append')} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-purple-500/20 text-purple-400 text-[10px] font-bold uppercase transition-all hover:bg-purple-500/30">
                                <Plus size={12} /> Add to Document
                              </button>
                            )}
                            {!splitViewActive && openedDocument && (
                              <button onClick={() => setSplitViewActive(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 text-white/50 text-[10px] font-bold uppercase transition-all hover:bg-white/10">
                                <Book size={12} /> View Editor
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {streamingMessage && (
                    <div className="chat-message asymmetric-assistant">
                      <ReactMarkdown>
                        {streamingMessage
                          .replace(/```document-update\s*([\s\S]*?)```/gi, '')
                          .replace(/```document-append\s*([\s\S]*?)```/gi, '')
                        }
                      </ReactMarkdown>
                    </div>
                  )}
                  {showThinking && !streamingMessage && (
                    <div className="thinking-pipeline p-4">
                      {thinkingSteps.map(step => (
                        <div key={step.id} className="flex items-center gap-2 text-xs mb-1 opacity-70">
                          <span className={step.status === 'completed' ? 'text-green-500' : 'text-blue-500'}>{step.status === 'completed' ? '✓' : '•'}</span>
                          <span>{step.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {!isMobile && (
              <div className="input-bar-wrapper">
                {pendingAttachments.length > 0 && (
                  <div className="flex gap-2 p-2 bg-white/5 overflow-x-auto">
                    {pendingAttachments.map((att, i) => (
                      <div key={i} className="flex items-center gap-1 px-2 py-1 bg-white/10 rounded-full text-[10px]">
                        <FileText size={10} /> {att.name}
                        <X size={10} onClick={() => setPendingAttachments(prev => prev.filter((_, idx) => idx !== i))} />
                      </div>
                    ))}
                  </div>
                )}
                <div className="input-bar p-3">
                  <div className="input-container flex items-center gap-2 bg-white/5 rounded-2xl p-2">
                    <button className="p-2" onClick={() => setShowAttachDropdown(!showAttachDropdown)}>
                      <Plus size={20} />
                    </button>
                    {showAttachDropdown && (
                      <div className="absolute bottom-full left-0 mb-2 bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden z-10">
                        <button className="flex items-center gap-2 p-3 hover:bg-white/5 w-full text-left" onClick={() => fileInputRef.current?.click()}>
                          <FileText size={16} /> Upload File
                        </button>
                        <button className="flex items-center gap-2 p-3 hover:bg-white/5 w-full text-left" onClick={handleGoogleDrivePick}>
                          <Clock size={16} /> Google Drive
                        </button>
                      </div>
                    )}
                    <input type="text" className="flex-1 bg-transparent outline-none" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={handleKeyPress} placeholder="Type a message..." />
                    <button className="p-2 bg-blue-600 rounded-xl" onClick={handleSend} disabled={!inputValue.trim() && pendingAttachments.length === 0}>
                      <ArrowUp size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {splitViewActive && !isMobile && (
            <div
              className="split-view-divider"
              onMouseDown={handleResizeMouseDown}
              style={{
                width: '4px',
                cursor: 'col-resize',
                background: isResizing ? '#3b82f6' : 'rgba(255,255,255,0.05)',
                height: '100%',
                zIndex: 10,
                transition: 'background 0.2s',
                flexShrink: 0
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#3b82f6'}
              onMouseLeave={e => !isResizing && (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
            />
          )}

          <div className="editor-container" ref={editorRef} style={splitViewActive ? { width: isMobile ? '100%' : `calc(${100 - splitPanelWidth}% - 4px)`, flex: 'none' } : {}}>
            <div className="editor-toolbar flex items-center gap-2 p-2 border-b border-white/5 overflow-x-auto no-scrollbar">
              {isMobile && (
                <button
                  className="p-1.5 mr-1 rounded-lg bg-white/5 border border-white/10"
                  onClick={() => {
                    setSplitViewActive(false);
                    onClose?.();
                  }}
                >
                  <ChevronLeft size={18} />
                </button>
              )}
              <button className="toolbar-btn p-1.5 rounded-lg hover:bg-white/5" onMouseDown={e => e.preventDefault()} onClick={() => handleToolbarCommand('undo')} title="Undo"><Undo size={14} /></button>
              <button className="toolbar-btn p-1.5 rounded-lg hover:bg-white/5" onMouseDown={e => e.preventDefault()} onClick={() => handleToolbarCommand('redo')} title="Redo"><Redo size={14} /></button>

              <div className="w-px h-4 bg-white/10 mx-1"></div>

              <button
                disabled={!editorReady}
                onClick={handleExport}
                className={`flex items-center gap-2 ${isMobile ? 'p-2' : 'px-3 py-1.5'} rounded-lg whitespace-nowrap
    ${editorReady ? 'bg-blue-600/20 text-blue-400' : 'bg-gray-500/20 text-gray-500 cursor-not-allowed'}
  `}
              >
                <Download size={14} className="flex-shrink-0" />
                {!isMobile && <span>Export</span>}
              </button>

              {/* <button
                disabled={!openedDocument}
                onClick={() => setIsEditCanvasOpen(true)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg
                  ${openedDocument ? 'bg-purple-600/20 text-purple-400' : 'bg-gray-500/20 text-gray-500 cursor-not-allowed'}
                `}
                title="Open Professional Editor"
              >
                <Sparkles size={14} />
                Advanced Editor
              </button> */}


              <div className="w-px h-4 bg-white/10 mx-1"></div>

              <ToolbarDropdown
                label="Normal"
                isOpen={activeDropdown === 'formatBlock'}
                onToggle={() => setActiveDropdown(activeDropdown === 'formatBlock' ? null : 'formatBlock')}
                currentValue={toolbarState.formatBlock}
                onSelect={(val) => handleToolbarCommand('formatBlock', val)}
                width="80px"
                options={[
                  { label: 'Normal', value: 'p' },
                  { label: 'Heading 1', value: 'h1' },
                  { label: 'Heading 2', value: 'h2' },
                  { label: 'Heading 3', value: 'h3' }
                ]}
              />

              <ToolbarDropdown
                label="Font"
                isOpen={activeDropdown === 'fontName'}
                onToggle={() => setActiveDropdown(activeDropdown === 'fontName' ? null : 'fontName')}
                currentValue={toolbarState.fontName}
                onSelect={(val) => handleToolbarCommand('fontName', val)}
                width="90px"
                options={[
                  { label: 'Inter', value: 'Inter, sans-serif' },
                  { label: 'Serif', value: 'serif' },
                  { label: 'Mono', value: 'monospace' },
                  { label: 'Arial', value: 'Arial' },
                  { label: 'Georgia', value: 'Georgia' },
                  { label: 'Times', value: 'Times New Roman' }
                ]}
              />

              <ToolbarDropdown
                label="Size"
                isOpen={activeDropdown === 'fontSize'}
                onToggle={() => setActiveDropdown(activeDropdown === 'fontSize' ? null : 'fontSize')}
                currentValue={toolbarState.fontSize}
                onSelect={(val) => handleToolbarCommand('fontSize', val)}
                width="60px"
                options={[
                  { label: 'XS', value: '1' },
                  { label: 'Small', value: '2' },
                  { label: 'Regular', value: '3' },
                  { label: 'Large', value: '4' },
                  { label: 'XL', value: '5' },
                  { label: 'XXL', value: '6' },
                  { label: 'Huge', value: '7' }
                ]}
              />

              <div className="w-px h-4 bg-white/10 mx-1"></div>

              <button className="toolbar-btn p-1.5 rounded-lg hover:bg-white/5" onMouseDown={e => e.preventDefault()} onClick={() => handleToolbarCommand('bold')} title="Bold"><span className="font-bold">B</span></button>
              <button className="toolbar-btn p-1.5 rounded-lg hover:bg-white/5" onMouseDown={e => e.preventDefault()} onClick={() => handleToolbarCommand('italic')} title="Italic"><span className="italic">I</span></button>
              <button className="toolbar-btn p-1.5 rounded-lg hover:bg-white/5" onMouseDown={e => e.preventDefault()} onClick={() => handleToolbarCommand('underline')} title="Underline"><span className="underline">U</span></button>

              <div className="w-px h-4 bg-white/10 mx-1"></div>

              <button className="toolbar-btn p-1.5 rounded-lg hover:bg-white/5" onMouseDown={e => e.preventDefault()} onClick={() => handleToolbarCommand('justifyLeft')} title="Align Left"><AlignLeft size={14} /></button>
              <button className="toolbar-btn p-1.5 rounded-lg hover:bg-white/5" onMouseDown={e => e.preventDefault()} onClick={() => handleToolbarCommand('justifyCenter')} title="Align Center"><AlignCenter size={14} /></button>
              <button className="toolbar-btn p-1.5 rounded-lg hover:bg-white/5" onMouseDown={e => e.preventDefault()} onClick={() => handleToolbarCommand('justifyRight')} title="Align Right"><AlignRight size={14} /></button>
              <button className="toolbar-btn p-1.5 rounded-lg hover:bg-white/5 text-red-400" onMouseDown={e => e.preventDefault()} onClick={() => handleToolbarCommand('removeFormat')} title="Clear Formatting"><Eraser size={14} /></button>

              <div className="ml-auto flex items-center gap-3 pr-1">
                <span className="text-[10px] opacity-40 mr-1">{isSaving ? 'Saving...' : 'Saved'}</span>
                <button
                  className="p-1.5 hover:bg-white/10 rounded-lg bg-white/5 border border-white/10 transition-colors"
                  onClick={async () => {
                    if (isDirtyRef.current) await saveDocument(editorContent);
                    setSplitViewActive(false);
                    onClose?.();
                  }}
                  title="Close Editor"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="editor-scroll p-8">
              <div
                ref={docPageRef}
                className="doc-page min-h-[1000px] outline-none"
                contentEditable="true"
                suppressContentEditableWarning={true}
                onInput={(e) => {
                  const html = e.currentTarget.innerHTML;
                  setEditorContent(html);
                  syncToolbarState();
                }}
                onMouseUp={syncToolbarState}
                onKeyUp={syncToolbarState}
              >
              </div>
            </div>
          </div>
        </div>
      </div>

      {isMobile && !splitViewActive && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 pb-[calc(112px+env(safe-area-inset-bottom,0px))]">
          <div className="flex flex-col gap-2">
            {pendingAttachments.length > 0 && (
              <div className="flex gap-2 p-2 bg-black/60 backdrop-blur-xl rounded-xl overflow-x-auto">
                {pendingAttachments.map((att, i) => (
                  <div key={i} className="flex items-center gap-1 px-3 py-1 bg-white/10 rounded-full text-xs">
                    <FileText size={12} /> {att.name}
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 bg-[#1a1a1a] border border-white/15 rounded-full p-2 h-14">
              <button className="p-2" onClick={() => setShowAttachDropdown(true)}><Plus size={24} /></button>
              <input type="text" className="flex-1 bg-transparent px-2 text-base outline-none" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyPress={handleKeyPress} placeholder="Ask anything..." />
              <button className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center" onClick={handleSend} disabled={!inputValue.trim() && pendingAttachments.length === 0}>
                <ArrowUp size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {isMobile && showAttachDropdown && (
        <MobileBottomSheet isOpen={showAttachDropdown} onClose={() => setShowAttachDropdown(false)} title="Attach">
          <button className="flex items-center gap-4 w-full p-4 hover:bg-white/5" onClick={() => { fileInputRef.current?.click(); setShowAttachDropdown(false); }}>
            <FileText size={20} /> <span>device Files</span>
          </button>
          <button className="flex items-center gap-4 w-full p-4 hover:bg-white/5" onClick={handleGoogleDrivePick}>
            <Clock size={20} /> <span>Google Drive</span>
          </button>
        </MobileBottomSheet>
      )}

      {isMobile && showMobileDocuments && (
        <MobileBottomSheet isOpen={showMobileDocuments} onClose={() => setShowMobileDocuments(false)} title="Documents">
          <div className="p-4 flex flex-col gap-4">
            <input type="search" className="w-full h-12 bg-white/5 rounded-xl px-4 outline-none" placeholder="Search..." value={contlySearchQuery} onChange={e => setContlySearchQuery(e.target.value)} />
            <div className="flex flex-col gap-2">
              {searchFilteredDocs.map(doc => (
                <DocumentCard key={doc.id} document={doc} onClick={handleDocumentClick} />
              ))}
            </div>
          </div>
        </MobileBottomSheet>
      )}

      {/* Edit Canvas Modal */}
      {openedDocument && (
        <EditCanvas
          isOpen={isEditCanvasOpen}
          file={openedDocument}
          onCancel={() => setIsEditCanvasOpen(false)}
          onSave={async (editedFile) => {
            try {
              setIsSaving(true);
              // 1. Re-extract content to update local HTML editor
              const newHtml = await extractFileContent(editedFile);

              // 2. Upload/Save the new DOCX binary and HTML content to Firestore
              // We reuse uploadFileAndPrepareData but since we are UPDATING, 
              // we just need to upload the blob and get the URL if we were pushing to storage,
              // but per documentContext update logic, we might just update the content if we don't want to re-upload to storage every time.
              // HOWEVER, the "EditCanvas" returns a .docx File/Blob. We SHOULD update the storage version too.

              // Simplified for now: Update the content in Firestore and local state
              setEditorContent(newHtml);
              if (docPageRef.current) docPageRef.current.innerHTML = newHtml;

              // We'll also update the document record
              await updateDocument(openedDocument.id, {
                content: newHtml,
                // In a full implementation, we'd also upload the editedFile to Storage here
                // but that requires more infrastructure changes to DocumentContext.
              });

              setIsEditCanvasOpen(false);
            } catch (err) {
              console.error("Save from Canvas failed:", err);
              alert("Failed to save changes from Advanced Editor");
            } finally {
              setIsSaving(false);
            }
          }}
        />
      )}
    </div>
  );
};

export default ContlyView;
